import { useEffect, useRef } from 'react';
import { toast } from '@doxyme/ui/components';
import { _getLogger } from '@doxyme/analytics';
import { Specs } from '@doxyme/extensions-common';
import { isProduction } from '../../../shared/isProduction';
import { processSideloadExtension } from '../../../features/extensions/sideloading';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import {
  RealtimeMessageType,
  type ExtensionSideloadSyncPayload,
} from '../../../features/realtime/realtime.types';

const logger = _getLogger('useSideloadSyncForPatient', {
  component: 'extensions',
});

/**
 * Subscribes to ExtensionSideloadSync realtime messages and applies each sideload
 * (validates baseUrl and manifest, then calls processSideloadExtension). Non-production only.
 * Deduplicates by manifest name so duplicate messages (e.g. provider sends on join and on sideload)
 * only result in one apply and one success toast per extension.
 */
export function useSideloadSyncForPatient(): void {
  const appliedNamesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const unsubscribe = subscribeToMessage({
      messageType: RealtimeMessageType.ExtensionSideloadSync,
      callback: (message) => {
        if (isProduction()) {
          return;
        }

        const payload = message.payload as
          | ExtensionSideloadSyncPayload
          | undefined;
        if (!payload?.sideloads?.length) {
          toast.error({
            title: 'No sideloads received',
          });
          return;
        }

        let appliedCount = 0;
        let hadInvalid = false;

        for (const { baseUrl, manifest } of payload.sideloads) {
          const name = manifest?.name;
          if (name && appliedNamesRef.current.has(name)) {
            continue;
          }

          try {
            new URL(baseUrl);
          } catch {
            logger.warn(
              '[useSideloadSyncForPatient] Invalid baseUrl, skipping',
              {
                baseUrl,
                manifestName: name,
              },
            );
            hadInvalid = true;
            continue;
          }

          const validation = Specs.isValidManifest(manifest);
          if (!validation.isValid) {
            logger.warn(
              '[useSideloadSyncForPatient] Invalid manifest, skipping',
              { error: validation.error, manifestName: name },
            );
            hadInvalid = true;
            continue;
          }

          try {
            processSideloadExtension({
              baseUrl,
              manifest,
            });
            if (name) {
              appliedNamesRef.current.add(name);
            }
            toast.success({
              title: `Sideloaded: ${manifest.name}`,
            });
            appliedCount += 1;
          } catch {
            toast.error({
              title: 'Failed to sideload. Check the console.',
            });
            hadInvalid = true;
          }
        }

        if (appliedCount === 0 && hadInvalid) {
          toast.error({
            title: 'No sideloads could be applied',
          });
        }
      },
    });

    return unsubscribe;
  }, []);
}
